/**
 * Clean engraved output. Given spelled pitches, draw real notation with VexFlow.
 * This is the "after" half of Transform — the ink stays untouched underneath.
 */

import { useEffect, useRef } from 'react';
import { Accidental, Formatter, Renderer, Stave, StaveNote, Voice } from 'vexflow';
import { ACCIDENTAL_ASCII, toVexKey, type Pitch } from '../theory/pitch';
import type { Clef } from '../theory/staff';
import { VEX_LINE_GAP } from './StaffPaper';

export interface ScoreProps {
  clef: Clef;
  /** Each entry is one chord (or a single note). */
  groups: Pitch[][];
  duration?: 'w' | 'h' | 'q';
  keySignature?: string;
  height?: number;
  /** Optional label drawn above the chord, e.g. "Cmaj7". */
  labels?: (string | null)[];
  /** Highlight colour for notes, used when showing an answer. */
  color?: string;
  /**
   * Octave annotation drawn on the clef. `8vb` is the guitar staff: the notes
   * are written an octave above the pitches the strings sound, which is the
   * convention every guitarist reads. Marking it is not decoration — an
   * unmarked transposing staff is a staff that lies about its own pitches.
   */
  octaveShift?: '8va' | '8vb';
}

export function Score({
  clef,
  groups,
  duration = 'w',
  keySignature,
  height = 190,
  labels,
  color,
  octaveShift,
}: ScoreProps) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.innerHTML = '';

    const width = Math.max(260, host.clientWidth || 320);
    const renderer = new Renderer(host, Renderer.Backends.SVG);
    renderer.resize(width, height);
    const ctx = renderer.getContext();

    // Scale the context rather than the stave's line spacing — see StaffPaper for why.
    const scale = 1.5;
    ctx.scale(scale, scale);
    const vw = width / scale;
    const vh = height / scale;

    const stave = new Stave(4, vh / 2 - VEX_LINE_GAP * 6, vw - 10);
    stave.addClef(clef, 'default', octaveShift);
    if (keySignature && keySignature !== 'C') stave.addKeySignature(keySignature);
    stave.setContext(ctx).draw();

    const playable = groups.filter((g) => g.length > 0);
    if (!playable.length) {
      const svg = host.querySelector('svg');
      if (svg) svg.setAttribute('class', 'score-svg');
      return;
    }

    const notes = playable.map((group) => {
      const sorted = [...group].sort(
        (a, b) => a.octave * 12 + a.letter.charCodeAt(0) - (b.octave * 12 + b.letter.charCodeAt(0)),
      );
      const note = new StaveNote({
        keys: sorted.map(toVexKey),
        duration,
        clef,
      });
      sorted.forEach((pitch, index) => {
        const glyph = ACCIDENTAL_ASCII[pitch.alter];
        if (glyph) note.addModifier(new Accidental(glyph), index);
      });
      if (color) note.setStyle({ fillStyle: color, strokeStyle: color });
      return note;
    });

    const voice = new Voice({ num_beats: notes.length * 4, beat_value: 4 });
    voice.setStrict(false);
    voice.addTickables(notes);
    new Formatter().joinVoices([voice]).format([voice], Math.max(60, vw - stave.getNoteStartX() - 20));
    voice.draw(ctx, stave);

    // Chord labels above the staff.
    if (labels) {
      ctx.save();
      ctx.setFont('Georgia, serif', 15);
      ctx.setFillStyle('#d4a74f');
      notes.forEach((note, i) => {
        const label = labels[i];
        if (!label) return;
        const bbox = note.getBoundingBox();
        const x = bbox ? bbox.getX() : stave.getNoteStartX() + i * 60;
        ctx.fillText(label, x - 4, stave.getYForLine(0) - 16);
      });
      ctx.restore();
    }

    const svg = host.querySelector('svg');
    if (svg) {
      svg.setAttribute('class', 'score-svg');
      svg.style.display = 'block';
    }
  }, [clef, groups, duration, keySignature, height, labels, color, octaveShift]);

  return <div ref={hostRef} className="score" role="img" aria-label="Engraved notation" />;
}
